import React, { useCallback, useEffect, useState } from "react";
import { commitMutation, graphql } from "react-relay";

import { faExclamationCircle, faHourglassHalf } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import { fetchQuery } from "relay-runtime";

import AppContext from "~/common/AppContext";
import { ReactComponent as GoogleDocsCheckMark } from "~/legacy-assets/images/logos/google_docs_checkmark.svg";
import { promptGoogleAuth } from "~/lib/auth";
import { environment } from "~/models";
import { Button, useModals } from "~/ui";
import { Input, Modal } from "~/ui/legacyFellowIndex";
import LoadingSpinner from "~/ui/loading/LoadingSpinner";

import { IncludeAIRecapCheckbox } from "../notes/IncludeAIRecapCheckbox";

import type { DocsExportModalAsyncTaskQuery } from "~/__generated__/DocsExportModalAsyncTaskQuery.graphql";
import type { DocsExportModalMutation as MutationType } from "~/__generated__/DocsExportModalMutation.graphql";
import type { DocsExportModalStreamHasMeetingRecordingsQuery } from "~/__generated__/DocsExportModalStreamHasMeetingRecordingsQuery.graphql";

import styles from "./css/DocsExportModal.module.css";

const ExportDocMutation = graphql`
	mutation DocsExportModalMutation($streamId: ID!, $title: String!, $noteId: ID, $includeMeetingRecap: Boolean) {
		exportStreamOrNoteToGoogleDocs(
			streamId: $streamId
			title: $title
			noteId: $noteId
			includeMeetingRecap: $includeMeetingRecap
		) {
			asyncResultId
		}
	}
`;

const AsyncTaskQuery = graphql`
	query DocsExportModalAsyncTaskQuery($taskId: JSON!) {
		viewer {
			asyncTask(taskId: $taskId) {
				taskId
				state
				info
			}
		}
	}
`;

const StreamHasMeetingRecordingsQuery = graphql`
	query DocsExportModalStreamHasMeetingRecordingsQuery($streamId: ID!) {
		noteStream(id: $streamId) {
			notes {
				edges {
					node {
						meetingRecordings {
							id
						}
					}
				}
			}
		}
	}
`;

interface Props {
	streamId: string;
	onClose: () => void;
	name: string;
	noteId?: string;
	onCancel?: () => void;
	hasMeetingRecordings: boolean | null;
}

interface State {
	title: string;
	view: "form" | "loading" | "complete" | "error" | "timeout";
	docsURL?: string;
	taskId: string | null;
	tries: number;
	authError?: boolean;
	includeMeetingRecap: boolean | null;
}

export function useDocsExportModal(streamId: string, streamName: string) {
	const [hasMeetingRecordings, setHasMeetingRecordings] = useState<boolean | null>(null);
	const modals = useModals();

	useEffect(() => {
		fetchQuery<DocsExportModalStreamHasMeetingRecordingsQuery>(environment, StreamHasMeetingRecordingsQuery, {
			streamId,
		})
			.toPromise()
			.then(resp => {
				const notes = resp?.noteStream?.notes?.edges || [];
				setHasMeetingRecordings(notes.some(note => (note?.node?.meetingRecordings?.length ?? 0) > 0));
			});
	}, [streamId]);

	const openModal = useCallback(
		() =>
			modals.open(({ close }) => (
				<DocsExportModal
					onClose={close}
					streamId={streamId}
					name={streamName}
					hasMeetingRecordings={hasMeetingRecordings}
				/>
			)),
		[modals, streamId, streamName, hasMeetingRecordings],
	);

	return openModal;
}

class DocsExportModal extends React.PureComponent<Props, State> {
	static contextType = AppContext;
	declare context: React.ContextType<typeof AppContext>;

	refreshStateTimer: number | null = null;

	constructor(props: Props) {
		super(props);

		this.state = {
			title: `[Fellow] ${props.name}`,
			includeMeetingRecap: props.hasMeetingRecordings,
			view: "form",
			taskId: null,
			tries: 0,
		};
	}

	startExport = () => {
		this.setState({ view: "loading" });
		commitMutation<MutationType>(environment, {
			mutation: ExportDocMutation,
			variables: {
				streamId: this.props.streamId,
				title: this.state.title,
				noteId: this.props.noteId,
				includeMeetingRecap: this.state.includeMeetingRecap,
			},
			onCompleted: ({ exportStreamOrNoteToGoogleDocs }, errors) => {
				if (errors?.length) {
					this.setState({ view: "error" });
				} else if (exportStreamOrNoteToGoogleDocs) {
					this.setState(
						{
							taskId: exportStreamOrNoteToGoogleDocs.asyncResultId,
						},
						() => {
							this.refreshStateTimer = window.setTimeout(this.refreshState, 2000);
						},
					);
				}
			},
		});
	};

	retryWithAuth = () => {
		promptGoogleAuth({ docs: true }).then(() => {
			this.startExport();
		});
	};

	refreshState = () => {
		if (!this.state.taskId) return;

		fetchQuery<DocsExportModalAsyncTaskQuery>(environment, AsyncTaskQuery, { taskId: this.state.taskId })
			.toPromise()
			.then(resp => {
				const asyncTask = resp?.viewer.asyncTask;
				if (!asyncTask) return;
				const { state, info } = asyncTask;

				if (state == "SUCCESS") {
					this.setState({
						tries: 0,
						view: "complete",
						docsURL: info.docsURL,
					});
				} else if (state == "PENDING") {
					this.setState(
						state => ({ tries: state.tries + 1 }),
						() => {
							this.refreshStateTimer = window.setTimeout(
								this.refreshState,
								Math.min(2 ** this.state.tries * 1000, 60 * 1000),
							);
						},
					);
				} else {
					const errorString: string = asyncTask.info?.error?.message || asyncTask.info?.error?.args || "";
					if (
						errorString.includes("insufficient_scope") ||
						errorString.includes("invalid_grant") ||
						errorString.includes("no refresh token")
					) {
						// If already auth errored, don't prompt again
						if (!this.state.authError) {
							this.setState({ authError: true, view: "error" }, () => {
								this.retryWithAuth();
							});
						} else {
							this.setState({ authError: false, view: "error" });
						}
						return;
					} else if (errorString.includes("timed out")) {
						this.setState({ view: "timeout" });
					} else {
						this.setState({ view: "error" });
					}
				}
			});
	};

	componentWillUnmount() {
		clearTimeout(this.refreshStateTimer ?? undefined);
	}

	openDocURL = () => {
		window.open(this.state.docsURL);
	};

	onTitleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		this.setState({ title: event.target.value });
	};

	onIncludeMeetingRecapChange = () => {
		this.setState(({ includeMeetingRecap }) => ({ includeMeetingRecap: !includeMeetingRecap }));
	};

	renderModalBody = () => {
		const { view, title } = this.state;

		if (view == "form") {
			return (
				<>
					<div className={styles.form}>
						<label className={styles.documentName}>Document name</label>
						<Input
							value={title}
							placeholder="Add a title for your export"
							onChange={this.onTitleChange}
							className={styles.titleInput}
							maxLength={255}
							name="docs_export_title"
						/>
					</div>
					<div className={styles.form}>
						<IncludeAIRecapCheckbox
							hasMeetingRecordings={this.props.hasMeetingRecordings}
							checked={this.state.includeMeetingRecap}
							onCheckedChange={this.onIncludeMeetingRecapChange}
						/>
					</div>
				</>
			);
		} else if (view == "loading") {
			return (
				<>
					<LoadingSpinner size="xs" className={styles.loader} />
					<div className={styles.loadingText}>
						Exporting to Google.
						<br />
						This may take a moment...
					</div>
				</>
			);
		} else if (view == "complete") {
			return (
				<>
					<div className={styles.exportCompleteDiv}>
						<GoogleDocsCheckMark className={styles.exportLogo} />
						<div className={styles.exportComplete}>Export complete</div>
						<div className={styles.exportTitle}>{title}</div>
					</div>
				</>
			);
		} else if (view == "error") {
			if (this.state.authError) {
				return (
					<div className={styles.error}>
						<FontAwesomeIcon icon={faExclamationCircle} />
						Authentication required. <br />
						Please{" "}
						<a href="#" onClick={this.retryWithAuth}>
							re-authenticate
						</a>{" "}
						with Google.
					</div>
				);
			}
			return (
				<div className={styles.error}>
					<FontAwesomeIcon icon={faExclamationCircle} />
					Sorry, something went wrong and we weren{"'"}t able to export this stream.
				</div>
			);
		} else if (view == "timeout") {
			return (
				<div className={styles.timeout}>
					<FontAwesomeIcon icon={faHourglassHalf} />
					<h4>Timeout!</h4>
					Check if your series titled <strong>{this.state.title}</strong> has made it to Google Docs — if not, please
					try again later.
				</div>
			);
		}
		return;
	};

	renderModalFooter = () => {
		const { view } = this.state;
		const { onClose, onCancel } = this.props;

		if (view == "form") {
			return (
				<Modal.Footer>
					<Button size="normal" variant="secondary" onClick={onCancel ?? onClose}>
						Cancel
					</Button>
					<Button size="normal" variant="primary" onClick={this.startExport}>
						Export
					</Button>
				</Modal.Footer>
			);
		} else if (view == "complete") {
			return (
				<Modal.Footer>
					<Button variant="primary" size="normal" onClick={this.openDocURL}>
						View in Google Drive
					</Button>
				</Modal.Footer>
			);
		}
		return;
	};

	render() {
		const { onClose, noteId } = this.props;
		const { view } = this.state;
		const noFooter = view == "loading" || view == "error";

		const modalContent = (
			<>
				<Modal.Header onClose={onClose}>
					<Modal.Title className={styles.headerTitle}>
						<div className={styles.googleDocsLogo} /> Export as Google Doc
					</Modal.Title>
				</Modal.Header>
				<Modal.Body className={cx(styles.body, { [styles.noFooter]: noFooter })}>{this.renderModalBody()}</Modal.Body>
				{this.renderModalFooter()}
			</>
		);

		if (noteId) {
			return modalContent;
		}
		return (
			<Modal onClose={onClose} width="23.75rem">
				{modalContent}
			</Modal>
		);
	}
}

export default DocsExportModal;
